import classNames from "classnames";
import Phaser from "phaser";
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import styles from "./App.module.scss";
import GameOver from "./components/GameOver/GameOver";
import AllLives from "./components/GameScoreAndLives/AllLives/AllLives";
import GameScore from "./components/GameScoreAndLives/GameScore/GameScore";
import MuteButton from "./components/MuteButton/MuteButton";
import { PauseOverlay } from "./components/PauseOverlay";
import QuestionContainer from "./components/QuestionContainer/QuestionContainer";
import StartScreen from "./components/StartScreen/StartScreen";
import {
  GAME_CONFIG,
  INITIAL_GAME_STATE,
  PHYSICS_CONFIG,
} from "./constants/game";
import { PacmanGame } from "./game/PacmanGame";
import { userHooks } from "./hooks/queries/useAuth";
import { useGameQuestions, GameConfig } from "./hooks/useGameQuestions";
import { useGameTimer } from "./hooks/useGameTimer";
import { useSwipeDetection } from "./hooks/useSwipeDetection";
import { useTabVisibility } from "./hooks/useTabVisibility";
import { useScreenSizeContext } from "./providers/ScreenSizeProvider";
import {
  GameSceneData,
  GameState,
  IGameData,
  PacmanGameEvents,
  PhaserGameInstance,
  QuestionData,
  RunningGameState,
} from "./types/game";
import { replayFailedRequests } from "./utils/api.utils";
import { PracticeMethods } from "./utils/numbers.utils";
import { getOverallScore } from "./utils/score.utils";

enum AppScreen {
  START_SCREEN = "start_screen",
  GAME_SCREEN = "game_screen",
  GAME_OVER_SCREEN = "game_over_screen",
}

const initialGameData: IGameData = {
  score: 0,
  totalCorrect: 0,
  totalIncorrect: 0,
  totalSums: 0,
  timeTaken: 0,
};

function App() {
  const gameRef = useRef<HTMLDivElement>(null);
  const [gameInstance, setGameInstance] = useState<PhaserGameInstance | null>(
    null
  );
  const gameInstanceRef = useRef<PhaserGameInstance | null>(null); // Ref to always have current gameInstance value
  const { isMobile } = useScreenSizeContext();
  const [gameState, setGameState] = useState<GameState>(GameState.MENU);
  const [isLoading, setIsLoading] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [appScreen, setAppScreen] = useState<AppScreen>(AppScreen.START_SCREEN);
  const [lives, setLives] = useState<number>(INITIAL_GAME_STATE.LIVES);
  const [isPausedByTab, setIsPausedByTab] = useState(false);
  const [runningGameState, setRunningGameState] = useState<RunningGameState>(
    RunningGameState.DEFAULT
  );
  const [gameData, setGameData] = useState<IGameData>(initialGameData);
  const [scaleFactor, setScaleFactor] = useState(1);
  const [smallDotsCount, setSmallDotsCount] = useState(0);
  const [powerPillsCount, setPowerPillsCount] = useState(0);
  const [correctAnswersCount, setCorrectAnswersCount] = useState(0);

  // Timer hook for tracking game play time
  // Automatically handles start/pause/resume/stop based on gameState
  const { timeTaken, resetTimer } = useGameTimer(gameState);

  // Auth
  const {
    data: gameProfile,
    isLoading: isProfileLoading,
    isError: isProfileError,
    refetch: refetchProfile,
    error: profileError,
    isSuccess: isProfileSuccess,
  } = userHooks.useGameProfile();

  const [isGameAssetsLoaded, setIsGameAssetsLoaded] = useState(false);
  const [assetLoadingProgress, setAssetLoadingProgress] = useState(0);

  // Memoize game config to prevent unnecessary re-renders
  const gameConfig: GameConfig = useMemo(() => {
    if (!gameProfile?.gameLevel)
      return {
        practiceMethod: PracticeMethods.Addition,
        digits: [1, 2],
        rows: 10,
        numberOfSums: 10,
      };

    return {
      practiceMethod: gameProfile.gameLevel.practiceMethod,
      digits: gameProfile.gameLevel.digits,
      rows: gameProfile.gameLevel.rows,
      numberOfSums: gameProfile.gameLevel.numberOfSums,
    };
  }, [gameProfile?.gameLevel]);

  // Use custom hook for question management with game configuration
  const { currentQuestion, generateNewQuestion, initializeQuestion } =
    useGameQuestions(gameConfig);

  // Use custom hook for tab visibility detection
  const { isVisible } = useTabVisibility();

  // Ref to always have current question value (avoid stale closure)
  const currentQuestionRef = useRef(currentQuestion);

  // Ref to track if pills have been placed for the current question
  // This prevents duplicate pill placement when gameState changes to PLAYING
  const pillsPlacedRef = useRef(false);

  // Keep gameInstanceRef in sync with gameInstance state
  useEffect(() => {
    gameInstanceRef.current = gameInstance;
  }, [gameInstance]);

  // Keep currentQuestionRef in sync with currentQuestion state
  useEffect(() => {
    currentQuestionRef.current = currentQuestion;
  }, [currentQuestion]);

  // Sync timer's timeTaken with gameData.timeTaken
  useEffect(() => {
    setGameData((gameData) => ({
      ...gameData,
      timeTaken,
    }));
  }, [timeTaken]);

  // Reinitialize question when game configuration is available
  // Only depend on gameConfig (not initializeQuestion) to avoid infinite loop
  useEffect(() => {
    if (gameConfig) {
      initializeQuestion();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gameConfig]);

  // Retry placing pills when gameConfig becomes available and game is PLAYING
  // This handles the case where the game starts before the profile loads
  useEffect(() => {
    if (
      gameConfig &&
      gameState === GameState.PLAYING &&
      gameInstance &&
      !pillsPlacedRef.current
    ) {
      console.log("Game config now available, retrying pill placement...");

      // Generate question and place pills directly
      const question = generateNewQuestion();
      if (question) {
        placePills({
          answers: question.answers,
          correctAnswer: question.correctAnswer,
        })
          .then((success) => {
            if (success) {
              pillsPlacedRef.current = true;
              console.log("Pills placed successfully after game config loaded");
            }
          })
          .catch((error) => {
            console.error(
              "Error placing pills after game config loaded:",
              error
            );
          });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gameConfig, gameState, gameInstance]);

  useEffect(() => {
    setGameData((gameData) => ({
      ...gameData,
      score: getOverallScore({
        smallDotsCount: smallDotsCount,
        powerPillsCount: powerPillsCount,
        correctAnswersCount: correctAnswersCount,
        gameConfig,
      }),
    }));
  }, [smallDotsCount, powerPillsCount, correctAnswersCount, gameConfig]);

  // Callback function to provide question data to PillManager
  // Called automatically by PillManager when game state becomes PLAYING
  const requestQuestion = (): QuestionData | null => {
    console.log("requestQuestion called from PillManager");
    console.log("gameConfig:", gameConfig);
    console.log("currentQuestion:", currentQuestion);

    // Guard: Ensure game config is available
    if (!gameConfig) {
      console.warn("Game config not available, cannot provide question");
      return null;
    }

    // Generate question if needed
    let question = currentQuestion;
    if (!question) {
      console.log("No current question, generating new one");
      question = generateNewQuestion();
      console.log("Generated question:", question);
      if (!question) {
        console.error("Failed to generate question");
        return null;
      }
    }

    const questionData = {
      answers: question.answers,
      correctAnswer: question.correctAnswer,
    };
    console.log("Returning question data:", questionData);
    return questionData;
  };

  const increaseSmallDotsCount = (count: number) => {
    setSmallDotsCount((prev) => prev + count);
  };

  const increasePowerPillsCount = (count: number) => {
    setPowerPillsCount((prev) => prev + count);
  };

  const increaseCorrectAnswersCount = (count: number) => {
    setCorrectAnswersCount((prev) => prev + count);
    setGameData((gameData) => ({
      ...gameData,
      totalCorrect: gameData.totalCorrect + count,
      totalSums: gameData.totalSums + count,
    }));
  };

  /**
   * Place number pills on the game board
   */
  const placePills = async (question: {
    answers: number[];
    correctAnswer: number;
  }): Promise<boolean> => {
    console.log("placePills called with:", question);

    // Use ref to get current gameInstance value (avoids closure issues)
    const currentGameInstance = gameInstanceRef.current;
    console.log("gameInstance available?", !!currentGameInstance);

    if (!currentGameInstance) {
      console.warn("Game not ready - gameInstance is null/undefined");
      return false;
    }

    try {
      const gameScene = currentGameInstance.scene.getScene(
        "PacmanGame"
      ) as unknown as PacmanGameEvents & {
        getCurrentState?: () => GameState;
      };
      console.log("gameScene retrieved:", !!gameScene);

      // Check game state from the game instance itself (more reliable than React state)
      if (gameScene && typeof gameScene.getCurrentState === "function") {
        const currentGameState = gameScene.getCurrentState();
        console.log("Current game state from game instance:", currentGameState);
        if (currentGameState !== GameState.PLAYING) {
          console.warn(
            `Cannot place pills - game state is ${currentGameState}`
          );
          return false;
        }
      } else {
        // Fallback to React state if getCurrentState is not available
        console.log(
          "getCurrentState not available, using React state:",
          gameState
        );
        if (gameState !== GameState.PLAYING) {
          console.warn(`Cannot place pills - game state is ${gameState}`);
          return false;
        }
      }

      console.log(
        "placeNumberPills method exists?",
        !!(gameScene && typeof gameScene.placeNumberPills === "function")
      );

      if (gameScene && typeof gameScene.placeNumberPills === "function") {
        console.log(
          "Calling placeNumberPills on game scene with answers:",
          question.answers,
          "correctAnswer:",
          question.correctAnswer
        );
        const success = await gameScene.placeNumberPills(
          question.answers,
          question.correctAnswer
        );
        console.log("placeNumberPills result:", success);
        return success;
      } else {
        console.error(
          "Game scene not found or placeNumberPills method missing",
          "gameScene:",
          !!gameScene,
          "method type:",
          typeof (gameScene as PacmanGameEvents)?.placeNumberPills
        );
        return false;
      }
    } catch (error) {
      console.error("Error placing pills:", error);
      return false;
    }
  };

  /**
   * Handle when a pill is eaten
   */
  const handlePillEaten = async (
    answer: number,
    correctAnswer: number
  ): Promise<void> => {
    if (!gameConfig) {
      console.error("Game config not available");
      return;
    }

    console.log(`Pill eaten: ${answer}, Correct answer: ${correctAnswer}`);

    // Clear the pills placed flag so new pills can be placed
    pillsPlacedRef.current = false;

    // Clear PillManager's pills placed flag
    const currentGameInstance = gameInstanceRef.current;
    if (currentGameInstance) {
      const gameScene = currentGameInstance.scene.getScene(
        "PacmanGame"
      ) as unknown as PacmanGameEvents;
      if (gameScene && typeof gameScene.clearPillsPlacedFlag === "function") {
        gameScene.clearPillsPlacedFlag();
        console.log("Cleared PillManager's pills placed flag");
      }
    }

    // Generate new question using the hook
    const newQuestion = generateNewQuestion();
    if (!newQuestion) {
      console.error("Failed to generate new question after pill eaten");
      return;
    }
    console.log("Generated new question:", newQuestion);

    // Place new pills immediately after eating
    console.log("About to place new pills after eating pill...");
    console.log("Current gameState:", gameState);
    console.log("gameInstance available:", !!gameInstanceRef.current);

    const success = await placePills({
      answers: newQuestion.answers,
      correctAnswer: newQuestion.correctAnswer,
    });

    console.log("placePills result after eating pill:", success);

    if (success) {
      // Mark pills as placed
      pillsPlacedRef.current = true;
      console.log("New pills placed after eating pill");
    } else {
      console.error("Failed to place new pills after eating pill");
    }
  };

  // /**
  //  * Handle wrong answer - called when Pacman eats wrong answer pill
  //  * The game's death system (pacmanDies) handles the life loss
  //  * We just need to ensure a new question is ready for the next life
  //  */
  // const handleWrongAnswer = (): void => {
  //   console.log("Wrong answer eaten - Pacman will die");
  //   // Generate new question for when player respawns using the hook
  //   generateNewQuestion();
  //   // Note: Pills will be placed when game restarts after death animation
  // };

  // Game event handlers with proper error handling
  const handleGameEvents = {
    start: async (): Promise<boolean> => {
      if (!gameInstance || isLoading) {
        console.warn("Game not ready or already loading");
        return false;
      }

      setIsLoading(true);
      try {
        const gameScene = gameInstance.scene.getScene(
          "PacmanGame"
        ) as unknown as PacmanGameEvents;
        if (gameScene && typeof gameScene.startGame === "function") {
          const success = await gameScene.startGame();
          console.log("Start game result:", success);

          // Reset pills placed flag when starting new game
          // Pills will be placed automatically by PillManager when gameState becomes PLAYING
          if (success) {
            pillsPlacedRef.current = false;
          }

          return success;
        } else {
          console.error("Game scene not found or startGame method missing");
          return false;
        }
      } catch (error) {
        console.error("Error starting game:", error);
        return false;
      } finally {
        setIsLoading(false);
      }
    },

    end: async (): Promise<boolean> => {
      if (!gameInstance || isLoading) {
        console.warn("Game not ready or already loading");
        return false;
      }

      setIsLoading(true);
      try {
        const gameScene = gameInstance.scene.getScene(
          "PacmanGame"
        ) as unknown as PacmanGameEvents;
        if (gameScene && typeof gameScene.endGame === "function") {
          const success = await gameScene.endGame();
          console.log("End game result:", success);
          return success;
        } else {
          console.error("Game scene not found or endGame method missing");
          return false;
        }
      } catch (error) {
        console.error("Error ending game:", error);
        return false;
      } finally {
        setIsLoading(false);
      }
    },

    pause: async (): Promise<boolean> => {
      if (!gameInstance || isLoading) {
        console.warn("Game not ready or already loading");
        return false;
      }

      setIsLoading(true);
      try {
        const gameScene = gameInstance.scene.getScene(
          "PacmanGame"
        ) as unknown as PacmanGameEvents;
        if (gameScene && typeof gameScene.pauseGame === "function") {
          const success = await gameScene.pauseGame();
          console.log("Pause game result:", success);
          return success;
        } else {
          console.error("Game scene not found or pauseGame method missing");
          return false;
        }
      } catch (error) {
        console.error("Error pausing game:", error);
        return false;
      } finally {
        setIsLoading(false);
      }
    },

    resume: async (): Promise<boolean> => {
      if (!gameInstance || isLoading) {
        console.warn("Game not ready or already loading");
        return false;
      }

      setIsLoading(true);
      try {
        const gameScene = gameInstance.scene.getScene(
          "PacmanGame"
        ) as unknown as PacmanGameEvents;
        if (gameScene && typeof gameScene.resumeGame === "function") {
          const success = await gameScene.resumeGame();
          console.log("Resume game result:", success);
          return success;
        } else {
          console.error("Game scene not found or resumeGame method missing");
          return false;
        }
      } catch (error) {
        console.error("Error resuming game:", error);
        return false;
      } finally {
        setIsLoading(false);
      }
    },

    reset: async (): Promise<boolean> => {
      if (!gameInstance || isLoading) {
        console.warn("Game not ready or already loading");
        return false;
      }
      setLives(INITIAL_GAME_STATE.LIVES);
      setGameData({ ...initialGameData });
      // Reset the counts that determine the score
      setSmallDotsCount(0);
      setPowerPillsCount(0);
      setCorrectAnswersCount(0);
      setIsLoading(true);
      try {
        const gameScene = gameInstance.scene.getScene(
          "PacmanGame"
        ) as unknown as PacmanGameEvents;
        if (gameScene && typeof gameScene.resetGame === "function") {
          const success = await gameScene.resetGame();
          console.log("Reset game result:", success);
          // Reset score and timer when game is reset
          if (success) {
            resetTimer(); // Reset timer and timeTaken
            setGameData((gameData) => ({
              ...gameData,
              score: 0,
            }));
            // Clear pills placed flag so pills can be placed again when game starts
            pillsPlacedRef.current = false;
            // Clear PillManager's pills placed flag
            const gameScene = gameInstance.scene.getScene(
              "PacmanGame"
            ) as unknown as PacmanGameEvents;
            if (
              gameScene &&
              typeof gameScene.clearPillsPlacedFlag === "function"
            ) {
              gameScene.clearPillsPlacedFlag();
            }
          }
          return success;
        } else {
          console.error("Game scene not found or resetGame method missing");
          return false;
        }
      } catch (error) {
        console.error("Error resetting game:", error);
        return false;
      } finally {
        setIsLoading(false);
      }
    },

    toggleMute: (): boolean => {
      if (!gameInstance) {
        console.warn("Game not ready");
        return false;
      }

      try {
        const gameScene = gameInstance.scene.getScene(
          "PacmanGame"
        ) as unknown as PacmanGameEvents;
        if (gameScene && typeof gameScene.toggleSoundMute === "function") {
          const newMuteState = gameScene.toggleSoundMute();
          setIsMuted(newMuteState);
          console.log("Sound mute toggled:", newMuteState);
          return newMuteState;
        } else {
          console.error(
            "Game scene not found or toggleSoundMute method missing"
          );
          return false;
        }
      } catch (error) {
        console.error("Error toggling mute:", error);
        return false;
      }
    },
  };

  // Handle life lost
  const handleLifeLost = (livesRemaining: number) => {
    console.log("Life lost! Lives remaining:", livesRemaining);
    setLives(livesRemaining);

    // Clear the pills placed flag so new pills can be placed after respawn
    pillsPlacedRef.current = false;

    // Clear PillManager's pills placed flag
    if (gameInstance) {
      const gameScene = gameInstance.scene.getScene(
        "PacmanGame"
      ) as unknown as PacmanGameEvents;
      if (gameScene && typeof gameScene.clearPillsPlacedFlag === "function") {
        gameScene.clearPillsPlacedFlag();
      }
    }

    // Generate question immediately (synchronously) so it appears right away
    const newQuestion = generateNewQuestion();
    console.log("Generated new question after death:", newQuestion);

    // Pills will be placed automatically by PillManager when gameState becomes PLAYING after respawn
    // The game state change happens after the respawn delay, so timing is handled automatically
  };

  // Handle game over
  const handleGameOver = () => {
    // Timer will be stopped automatically by useGameTimer hook when gameState changes to GAME_OVER
    setAppScreen(AppScreen.GAME_OVER_SCREEN);
    setGameState(GameState.GAME_OVER);
  };

  // Handle resume from pause
  const handleResume = async (): Promise<void> => {
    if (!gameInstance) {
      console.warn("Game not ready");
      return;
    }

    try {
      const gameScene = gameInstance.scene.getScene(
        "PacmanGame"
      ) as unknown as PacmanGameEvents;

      if (gameScene && typeof gameScene.resumeGame === "function") {
        const success = await gameScene.resumeGame();
        if (success) {
          setIsPausedByTab(false);
          // Game state will be updated by onGameStateChange callback
        }
      } else {
        // Fallback: manually resume the scene
        const scene = gameInstance.scene.getScene("PacmanGame");
        if (scene && scene.scene.isPaused()) {
          scene.scene.resume();
          setIsPausedByTab(false);
          setGameState(GameState.PLAYING);
        }
      }
    } catch (error) {
      console.error("Error resuming game:", error);
    }
  };

  useEffect(() => {
    // Only create game if we have a ref and no existing game instance
    if (gameRef.current && !gameInstance) {
      console.log("Creating game instance");

      // Add a flag to prevent double initialization
      if (gameRef.current.dataset.gameInitialized === "true") {
        console.log("Game already initialized on this element, skipping");
        return;
      }

      try {
        // setIsGameInitializing(true);

        // Prepare callbacks BEFORE creating the game so they're available in init()
        const gameCallbacks: GameSceneData = {
          setIsGameAssetsLoaded: setIsGameAssetsLoaded,
          setAssetLoadingProgress: setAssetLoadingProgress,
          increaseSmallDotsCount: increaseSmallDotsCount,
          increasePowerPillsCount: increasePowerPillsCount,
          increaseCorrectAnswersCount: increaseCorrectAnswersCount,
          onGameOver: handleGameOver,
          onLifeLost: handleLifeLost,
          onGameStateChange: setGameState,
          onPillEaten: handlePillEaten,
          setRunningGameState: setRunningGameState,
          onGamePaused: () => {
            console.log("Game paused by tab visibility");
            setIsPausedByTab(true);
            setGameState(GameState.PAUSED);
          },
          requestQuestion: requestQuestion,
          // onWrongAnswer: handleWrongAnswer,
        };

        const config = {
          type: Phaser.AUTO,
          width: GAME_CONFIG.WIDTH,
          height: GAME_CONFIG.HEIGHT,
          parent: gameRef.current,
          backgroundColor: GAME_CONFIG.BACKGROUND_COLOR,
          physics: {
            default: "arcade",
            arcade: {
              gravity: {
                x: PHYSICS_CONFIG.GRAVITY_X,
                y: PHYSICS_CONFIG.GRAVITY_Y,
              },
              debug: PHYSICS_CONFIG.DEBUG,
            },
          },
          scene: PacmanGame,
          scale: {
            mode: Phaser.Scale.FIT,
            autoCenter: Phaser.Scale.CENTER_BOTH,
          },
          // Explicitly disable Phaser's built-in pause-on-blur behavior
          // so our custom tab visibility logic is the single source of truth
          pauseOnBlur: false,

          onpause: () => {
            console.log("Game paused");
          },
          onresume: () => {
            console.log("Game resumed");
          },
          onreset: () => {
            console.log("Game reset");
          },
        };

        const game = new Phaser.Game(config);

        // Store the typed callbacks in the registry
        // Set immediately after game creation so it's available in create()
        game.registry.set("gameCallbacks", gameCallbacks);
        console.log("Game created successfully:", game);

        // Mark this element as having a game initialized
        if (gameRef.current) {
          gameRef.current.dataset.gameInitialized = "true";
        }

        setGameInstance(game);

        // Generate initial question when game is ready using the hook
        // Only initialize if gameConfig is available, otherwise it will be initialized
        // when gameConfig becomes available via the useEffect hook
        if (gameConfig) {
          initializeQuestion();
        }

        // setIsGameInitializing(false);
      } catch (error) {
        console.error("Error creating game:", error);
        // setIsGameInitializing(false);
      }
    }

    // Cleanup function
    return () => {
      // Use ref to get the most current instance (avoids stale closure issues)
      const currentGame = gameInstanceRef.current;
      if (currentGame) {
        console.log("Component unmounting - destroying game");
        currentGame.destroy(true);

        // Clean up the flag
        if (gameRef.current) {
          gameRef.current.dataset.gameInitialized = "false";
        }
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty dependency array - cleanup only runs on unmount

  // Handle swipe direction changes
  const handleSwipeDirection = (direction: string) => {
    // Send direction to the game if it's running
    if (
      gameInstance &&
      gameInstance.scene &&
      gameInstance.scene.scenes.length > 0
    ) {
      const gameScene = gameInstance.scene.getScene(
        "PacmanGame"
      ) as unknown as PacmanGameEvents;
      console.log("Game scene found:", !!gameScene);

      if (gameScene && "cursors" in gameScene) {
        const pacmanGameScene = gameScene as PacmanGameEvents & {
          cursors: Phaser.Types.Input.Keyboard.CursorKeys;
        };
        console.log("Cursors found, updating direction:", direction);

        // Reset all directions first
        pacmanGameScene.cursors.left.isDown = false;
        pacmanGameScene.cursors.right.isDown = false;
        pacmanGameScene.cursors.up.isDown = false;
        pacmanGameScene.cursors.down.isDown = false;

        // Set the active direction
        switch (direction) {
          case "left":
            pacmanGameScene.cursors.left.isDown = true;
            break;
          case "right":
            pacmanGameScene.cursors.right.isDown = true;
            break;
          case "up":
            pacmanGameScene.cursors.up.isDown = true;
            break;
          case "down":
            pacmanGameScene.cursors.down.isDown = true;
            break;
          default:
            // No direction - all keys are false
            break;
        }

        console.log("Updated cursors:", {
          left: pacmanGameScene.cursors.left.isDown,
          right: pacmanGameScene.cursors.right.isDown,
          up: pacmanGameScene.cursors.up.isDown,
          down: pacmanGameScene.cursors.down.isDown,
        });
      } else {
        console.log("No cursors found in game scene");
      }

      // Alternative approach: Set a custom input state on the game scene
      if (gameScene) {
        const sceneWithJoystick = gameScene as PacmanGameEvents & {
          joystickInput?: string | null;
        };
        sceneWithJoystick.joystickInput = direction;
        console.log("Set swipe input on game scene:", direction);
      }
    } else {
      console.log("Game instance or scene not ready:", {
        gameInstance: !!gameInstance,
        scenes: gameInstance?.scene?.scenes.length || 0,
      });
    }
  };

  // Initialize swipe detection for mobile devices
  useSwipeDetection({
    onSwipe: handleSwipeDirection,
    threshold: 50,
    preventDefault: true,
    isActive: isMobile && runningGameState === RunningGameState.RUNNING,
    targetElement: gameRef.current,
  });

  const handleStartGame = () => {
    // Check me
    if (gameProfile) {
      setAppScreen(AppScreen.GAME_SCREEN);
      handleGameEvents.start();
    } else {
      refetchProfile();
    }
  };

  const handleRestartGame = async () => {
    setAppScreen(AppScreen.GAME_SCREEN);
    await handleGameEvents.reset();
    handleGameEvents.start();
  };

  useEffect(() => {
    if (isProfileSuccess) {
      replayFailedRequests();
    }
  }, [isProfileSuccess]);

  // Reset isPausedByTab when game state changes away from PAUSED
  useEffect(() => {
    if (gameState !== GameState.PAUSED && isPausedByTab) {
      setIsPausedByTab(false);
    }
  }, [gameState, isPausedByTab]);

  // Handle tab visibility changes for pause/resume
  useEffect(() => {
    // Only handle pause/resume if game is initialized and in appropriate state
    if (!gameInstance || isLoading) {
      return;
    }

    const currentGameInstance = gameInstanceRef.current;
    if (!currentGameInstance) {
      return;
    }

    // When tab becomes hidden
    if (!isVisible) {
      // Only pause if game is in PLAYING state and not already paused by tab
      if (gameState === GameState.PLAYING && !isPausedByTab) {
        const gameScene = currentGameInstance.scene.getScene(
          "PacmanGame"
        ) as unknown as PacmanGameEvents;

        if (gameScene && typeof gameScene.pauseGame === "function") {
          gameScene.pauseGame().then((success) => {
            if (success) {
              // Call onGamePaused callback to notify React
              const scene = currentGameInstance.scene.getScene(
                "PacmanGame"
              ) as unknown as PacmanGameEvents & { onGamePaused?: () => void };
              if (scene && scene.onGamePaused) {
                scene.onGamePaused();
              } else {
                // Fallback: directly update state
                setIsPausedByTab(true);
                setGameState(GameState.PAUSED);
              }
              console.log("Game paused due to tab visibility change");
            }
          });
        } else {
          // Fallback: manually pause the scene
          const scene = currentGameInstance.scene.getScene("PacmanGame");
          if (scene && !scene.scene.isPaused()) {
            scene.scene.pause();
            setIsPausedByTab(true);
            setGameState(GameState.PAUSED);
          }
        }
      }
    }
    // Note: We don't auto-resume when tab becomes visible
    // User must click the resume button
  }, [isVisible, gameInstance, gameState, isLoading, isPausedByTab]);

  useLayoutEffect(() => {
    // Calculate scale factor based on available screen height
    // Maintain aspect ratio: 1392/1632 (width will auto-calculate from height)
    const calculateScale = () => {
      // Get available height (accounting for UI elements, padding, etc.)
      const availableHeight = window.innerHeight - 100; // Use 70% of screen height
      const scale = availableHeight / GAME_CONFIG.HEIGHT;

      setScaleFactor(scale);
    };

    calculateScale();

    // Recalculate on window resize
    window.addEventListener("resize", calculateScale);
    return () => window.removeEventListener("resize", calculateScale);
  }, []);

  // Extract error message from profileError
  const errorMessage = profileError?.message || undefined;

  return (
    <div className="min-h-screen bg-gray-900 flex flex-col items-center justify-center">
      <MuteButton
        isMuted={isMuted}
        onToggleMute={() => handleGameEvents.toggleMute()}
      />
      {appScreen === AppScreen.START_SCREEN && (
        <StartScreen
          assetLoadingProgress={assetLoadingProgress}
          isGameAssetsLoaded={isGameAssetsLoaded}
          onStartGame={handleStartGame}
          isError={isProfileError}
          isLoading={isProfileLoading}
          highScore={gameProfile?.profile?.highScore}
          errorMessage={errorMessage}
        />
      )}
      <GameOver
        gameData={gameData}
        highScore={gameProfile?.profile?.highScore || 0}
        userName={gameProfile?.name || "Guest Player"}
        onRestart={handleRestartGame}
        onExit={() => setAppScreen(AppScreen.START_SCREEN)}
        isGameOver={appScreen === AppScreen.GAME_OVER_SCREEN}
      />

      <div
        className={classNames(
          styles.gameContainer,
          appScreen === AppScreen.GAME_SCREEN && styles.visible,
          "p-28"
        )}
      >
        <div className={classNames(styles.playArea)}>
          {/* Game Controls */}
          {/* <GameControls
          gameState={gameState}
          isLoading={isLoading}
          isGameInitializing={isGameInitializing}
          isMuted={isMuted}
          currentQuestion={currentQuestion}
          onStart={() => handleGameEvents.start()}
          onPause={() => handleGameEvents.pause()}
          onResume={() => handleGameEvents.resume()}
          onEnd={() => handleGameEvents.end()}
          onReset={() => handleGameEvents.reset()}
          onToggleMute={() => handleGameEvents.toggleMute()}
        /> */}
          <div className={styles.gameScoreAndLivesContainer}>
            <GameScore title="Game Score" score={gameData.score} />

            {!isMobile && <AllLives lives={lives} direction="horizontal" />}

            <GameScore
              title="High Score"
              score={gameProfile?.profile?.highScore || 0}
            />

            {isMobile && <AllLives lives={lives} direction="horizontal" />}
          </div>
          <div className={styles.mainContainer}>
            <QuestionContainer
              question={currentQuestion}
              direction={isMobile ? "horizontal" : "vertical"}
              gameState={gameState}
            />

            {/* Game Container */}
            <div
              ref={gameRef}
              className={classNames(styles.gameCanvas)}
              style={{
                width: `${GAME_CONFIG.WIDTH * scaleFactor}px`,
                height: `${GAME_CONFIG.HEIGHT * scaleFactor}px`,
                maxWidth: "98vw",
                maxHeight: "90vh",
              }}
            />

            {/* Mobile Swipe Instructions */}
            {/* {isMobile && gameState === GameState.PLAYING && (
            <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2">
              <div className="text-center">
                <p className="text-yellow-400 font-bold text-sm mb-2">
                  Swipe on game area to move Pacman
                </p>
                <div className="flex justify-center space-x-4 text-xs text-gray-400">
                  <span>↑ Up</span>
                  <span>↓ Down</span>
                  <span>← Left</span>
                  <span>→ Right</span>
                </div>
              </div>
            </div>
          )} */}
          </div>
        </div>
      </div>

      {/* Pause Overlay */}
      <PauseOverlay
        show={
          gameState === GameState.PAUSED &&
          isPausedByTab &&
          appScreen === AppScreen.GAME_SCREEN
        }
        onResume={handleResume}
        isPausedByTab={isPausedByTab}
      />
    </div>
  );
}

export default App;
